import posthog from "posthog-js";
import { getSubdomain } from "utils/subdomain";
import { API_EVENTS } from "constants/apiEvents";

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
const posthogFlag = import.meta.env.VITE_POSTHOG_ENABLED === "true";

// Production only, so dev and staging traffic never mixes into the data.
// `VITE_POSTHOG_ENABLED=true` is set in `.env.production` alone — the dev,
// staging and preview workflows swap that file out — and a production build is
// required as well, so `npm run dev` never tracks whatever `.env.local` holds.
export const isPostHogEnabled = Boolean(
  import.meta.env.PROD && posthogFlag && posthogKey && posthogHost
);

if (isPostHogEnabled) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    capture_pageview: "history_change",
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    defaults: "2026-05-30",
    logs: {
      serviceName: "amer-admin-webapp",
      environment: import.meta.env.MODE,
    },
  });
}

export const posthogLogger = {
  info(message, attributes) {
    if (isPostHogEnabled) posthog.logger.info(message, attributes);
  },
};

export const identifyPostHogUser = (user) => {
  if (!isPostHogEnabled || user?.id == null) return;

  const name = [user.en_fname ?? user.fname, user.en_lname ?? user.lname]
    .filter(Boolean)
    .join(" ");
  const properties = {};

  if (user.email) properties.email = user.email;
  if (name) properties.name = name;

  // User ids are only unique within a tenant, so the tenant is part of the
  // distinct id; otherwise user 12 of every office would merge into one person.
  const tenant = getSubdomain();
  if (tenant) properties.tenant = tenant;

  posthog.identify(
    tenant ? `${tenant}:${user.id}` : String(user.id),
    properties
  );
  if (tenant) posthog.group("tenant", tenant);
};

export const resetPostHogUser = () => {
  if (isPostHogEnabled) posthog.reset();
};

export const capturePostHogException = (error, properties) => {
  if (isPostHogEnabled) posthog.captureException(error, properties);
};

// ─── Request-driven events ───────────────────────────────────────────────────
// `customFetch` hands every successful request to `trackApiRequest`, which
// captures the product event it stands for. The events themselves — one per
// write endpoint — are declared in `API_EVENTS` (constants/apiEvents.js), so a
// screen adds no analytics code of its own.

const readField = (body, key) =>
  body instanceof FormData ? body.get(key) : body?.[key];

// Laravel method spoofing: a FormData PUT travels as POST with `_method=PUT`.
const effectiveMethod = (method, body) =>
  String(readField(body, "_method") || method || "GET").toUpperCase();

const toPathPattern = (path) =>
  new RegExp(
    `^${path
      .split("/")
      .map((segment) =>
        segment.startsWith(":")
          ? "[^/]+"
          : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      )
      .join("/")}$`
  );

let compiledEvents;
const getCompiledEvents = () => {
  compiledEvents ??= API_EVENTS.map((entry) => ({
    ...entry,
    pattern: toPathPattern(entry.path),
  }));
  return compiledEvents;
};

const withoutEmpty = (properties) =>
  Object.fromEntries(
    Object.entries(properties || {}).filter(([, value]) => value != null)
  );

/**
 * Captures the product event a successful API request stands for, if any.
 * Call it only after the API answered `status: true`.
 *
 * @param {{ url: string, method?: string, body?: FormData|object, response?: object, meta?: object }} request
 */
export const trackApiRequest = (request) => {
  if (!isPostHogEnabled || !request?.url) return;

  try {
    const url = request.url.split("?")[0].replace(/^\/+/, "");
    const method = effectiveMethod(request.method, request.body);
    const context = {
      ...request,
      url,
      method,
      field: (key) => readField(request.body, key),
    };

    const entry = getCompiledEvents().find(
      (candidate) =>
        (!candidate.method || candidate.method === method) &&
        candidate.pattern.test(url) &&
        (!candidate.when || candidate.when(context))
    );
    if (!entry) return;

    const properties = withoutEmpty({
      ...Object.fromEntries(
        (entry.props || []).map((key) => [key, context.field(key)])
      ),
      ...entry.properties?.(context),
    });
    posthog.capture(entry.event, properties);
    if (entry.log) posthogLogger.info(entry.log, properties);
  } catch {
    // Analytics must never break the request that triggered it.
  }
};

export default posthog;
